<script setup>
import { computed } from 'vue'
import { 
  ToastProvider,
  ToastRoot,
  ToastViewport,
  ToastTitle,
  ToastDescription,
  ToastClose
} from 'reka-ui'
import { 
  CheckCircle, 
  AlertCircle, 
  Info, 
  X 
} from 'lucide-vue-next'
import { useToast } from '../../composables/useToast.js'

const props = defineProps({
  position: {
    type: String,
    default: 'bottom-right',
    validator: (value) => ['top-right', 'top-left', 'bottom-right', 'bottom-left', 'top-center', 'bottom-center'].includes(value)
  },
  maxToasts: {
    type: Number,
    default: 5
  },
  closeButton: {
    type: Boolean,
    default: true
  },
  pauseOnHover: {
    type: Boolean,
    default: true
  }
})

const { toasts, removeToast } = useToast()

const getToastIcon = (type) => {
  const icons = {
    success: CheckCircle,
    danger: AlertCircle,
    primary: Info,
    secondary: Info
  }
  return icons[type] || Info
}

const getToastClasses = (type) => {
  const baseClasses = 'group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-base border p-4 pr-8 shadow-base transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--reka-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--reka-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full'

  const typeClasses = {
    success: 'border-success-muted bg-success-light text-success',
    danger: 'border-danger-muted bg-danger-light text-danger',
    primary: 'border-primary-muted bg-primary-light text-primary',
    secondary: 'border-secondary-muted bg-secondary-light text-secondary'
  }

  return `${baseClasses} ${typeClasses[type] || typeClasses.primary}`
}

const getIconClasses = (type) => {
  const iconClasses = {
    success: 'text-success',
    danger: 'text-danger', 
    primary: 'text-primary',
    secondary: 'text-secondary'
  }
  return iconClasses[type] || iconClasses.primary
}

const positionClasses = computed(() => {
  const positions = {
    'top-right': 'fixed top-0 right-0 flex max-h-screen w-full flex-col p-4 md:max-w-[420px]',
    'top-left': 'fixed top-0 left-0 flex max-h-screen w-full flex-col p-4 md:max-w-[420px]',
    'bottom-right': 'fixed bottom-0 right-0 flex max-h-screen w-full flex-col-reverse p-4 md:max-w-[420px]',
    'bottom-left': 'fixed bottom-0 left-0 flex max-h-screen w-full flex-col-reverse p-4 md:max-w-[420px]',
    'top-center': 'fixed top-0 left-1/2 -translate-x-1/2 flex max-h-screen w-full flex-col p-4 md:max-w-[420px]',
    'bottom-center': 'fixed bottom-0 left-1/2 -translate-x-1/2 flex max-h-screen w-full flex-col-reverse p-4 md:max-w-[420px]'
  }
  return positions[props.position]
})

const visibleToasts = computed(() => {
  return toasts.slice(0, props.maxToasts)
})

const swipeDirection = computed(() => {
  return props.position.includes('left') ? 'left' : 'right'
})
</script>

<template>
  <ToastProvider 
    :duration="0"
    :swipe-direction="swipeDirection"
  >
    <template v-for="toast in visibleToasts" :key="toast.id">
      <ToastRoot
        :open="true"
        :type="toast.type === 'danger' ? 'foreground' : 'background'"
        :class="[
          getToastClasses(toast.type),
          pauseOnHover ? 'hover:pause' : ''
        ]"
        @update:open="(open) => !open && removeToast(toast.id)"
      >
        <div class="flex items-start space-x-3">
          <component 
            :is="getToastIcon(toast.type)"
            :size="20"
            :class="[getIconClasses(toast.type), 'flex-shrink-0 mt-0.5']"
          />
          
          <div class="flex-1 min-w-0">
            <ToastTitle 
              v-if="toast.title"
              class="text-sm font-medium mb-1 text-gray-900"
            >
              {{ toast.title }}
            </ToastTitle>
            
            <ToastDescription class="text-sm opacity-90 text-gray-700">
              {{ toast.message }}
            </ToastDescription>
          </div>
        </div>

        <ToastClose 
          v-if="closeButton"
          class="absolute right-2 top-2 rounded-base p-1 text-current hover:bg-current hover:bg-opacity-10 opacity-70 hover:opacity-100 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-current focus-visible:ring-2 focus-visible:ring-current group-hover:opacity-100 transition-all"
          @click="removeToast(toast.id)"
          aria-label="Close notification"
        >
          <X :size="14" />
        </ToastClose>
      </ToastRoot>
    </template>

    <ToastViewport :class="['z-[10000]', positionClasses]" />
  </ToastProvider>
</template>

<style scoped>
.hover\:pause:hover {
  animation-play-state: paused;
}
</style>